import React, { useMemo, useState } from 'react';
import { HDate, gematriya } from '@hebcal/core';

/**
 * Date input with a לועזי/עברי toggle. Gregorian mode is a native date input
 * with the Hebrew date shown underneath; Hebrew mode is day/month/year selects
 * that convert to a Gregorian ISO date on change (the stored value is always
 * Gregorian yyyy-mm-dd, so callers keep a single canonical format).
 *
 * `InlineHebrewDate` (exported below) is the compact variant used inside
 * signature-document paragraphs, where a full three-select row would not fit
 * in the flow of a sentence — it shows the picked date as text on a ruled line
 * and opens the same day/month/year picker in a small popover.
 */

// hebcal month numbers, ordered for display from Tishrei (school-year order).
const MONTHS_COMMON: { m: number; l: string }[] = [
  { m: 7, l: 'תשרי' }, { m: 8, l: 'חשון' }, { m: 9, l: 'כסלו' }, { m: 10, l: 'טבת' },
  { m: 11, l: 'שבט' }, { m: 12, l: 'אדר' },
  { m: 1, l: 'ניסן' }, { m: 2, l: 'אייר' }, { m: 3, l: 'סיון' }, { m: 4, l: 'תמוז' },
  { m: 5, l: 'אב' }, { m: 6, l: 'אלול' },
];
const MONTHS_LEAP: { m: number; l: string }[] = [
  { m: 7, l: 'תשרי' }, { m: 8, l: 'חשון' }, { m: 9, l: 'כסלו' }, { m: 10, l: 'טבת' },
  { m: 11, l: 'שבט' }, { m: 12, l: "אדר א'" }, { m: 13, l: "אדר ב'" },
  { m: 1, l: 'ניסן' }, { m: 2, l: 'אייר' }, { m: 3, l: 'סיון' }, { m: 4, l: 'תמוז' },
  { m: 5, l: 'אב' }, { m: 6, l: 'אלול' },
];

const toIso = (d: Date) => d.toLocaleDateString('en-CA');

/** Gregorian ISO → "י״ד באב תשפ״ו" ('' when the input isn't a real date). */
export const hebrewDateLabel = (iso: string): string => {
  try {
    if (!iso) return '';
    const hd = new HDate(new Date(`${iso}T12:00:00`));
    const months = HDate.isLeapYear(hd.getFullYear()) ? MONTHS_LEAP : MONTHS_COMMON;
    const monthName = months.find(x => x.m === hd.getMonth())?.l || hd.getMonthName();
    return `${gematriya(hd.getDate())} ${monthName} ${gematriya(hd.getFullYear())}`;
  } catch { return ''; }
};

/** Gregorian ISO → "16.8.2026" ('' when empty/invalid). */
export const gregDateLabel = (iso: string): string => {
  try {
    if (!iso) return '';
    const d = new Date(`${iso}T12:00:00`);
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString('he-IL');
  } catch { return ''; }
};

/** Hebrew day/month/year → Gregorian ISO, clamping a day/month the year lacks. */
const hebrewToIso = (y: number, m: number, d: number): string | null => {
  try {
    const mm = HDate.isLeapYear(y) ? m : (m === 13 ? 12 : m);
    const dd = Math.min(d, HDate.daysInMonth(mm, y));
    return toIso(new HDate(dd, mm, y).greg());
  } catch { return null; }
};

/** Day/month/year selects over the Hebrew calendar. Emits Gregorian ISO. */
const HebrewParts: React.FC<{
  value: string;
  onChange: (iso: string) => void;
  className: string;
  style?: React.CSSProperties;
  /** 'span' when rendered inside a <p> (signature documents) — a <div> there
   *  is invalid nesting and React warns about it. */
  as?: 'div' | 'span';
}> = ({ value, onChange, className, style, as }) => {
  // Working parts, kept in sync with the current value (or today) — without the
  // resync, opening the Hebrew picker after typing a Gregorian date would show
  // today's Hebrew date and silently overwrite the stored value on touch.
  const seed = useMemo(() => {
    try { return new HDate(value ? new Date(`${value}T12:00:00`) : new Date()); }
    catch { return new HDate(new Date()); }
  }, [value]);
  const [hy, setHy] = useState(seed.getFullYear());
  const [hm, setHm] = useState(seed.getMonth());
  const [hd, setHd] = useState(seed.getDate());
  React.useEffect(() => { setHy(seed.getFullYear()); setHm(seed.getMonth()); setHd(seed.getDate()); }, [seed]);

  const months = HDate.isLeapYear(hy) ? MONTHS_LEAP : MONTHS_COMMON;
  const daysInMonth = (() => { try { return HDate.daysInMonth(hm, hy); } catch { return 30; } })();

  const emit = (y: number, m: number, d: number) => {
    const iso = hebrewToIso(y, m, d);
    if (iso) onChange(iso);
  };

  const yearNow = new HDate(new Date()).getFullYear();
  const years = Array.from({ length: 121 }, (_, i) => yearNow + 1 - i); // this year+1 back 120y

  const Wrap: any = as || 'div';
  return (
    <Wrap className="flex gap-1 flex-1">
      <select className={className} style={style} value={hd} onChange={e => { const d = Number(e.target.value); setHd(d); emit(hy, hm, d); }}>
        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(d => <option key={d} value={d}>{gematriya(d)}</option>)}
      </select>
      <select className={className} style={style} value={hm} onChange={e => { const m = Number(e.target.value); setHm(m); emit(hy, m, hd); }}>
        {months.map(x => <option key={x.m} value={x.m}>{x.l}</option>)}
      </select>
      <select className={className} style={style} value={hy} onChange={e => { const y = Number(e.target.value); setHy(y); emit(y, hm, hd); }}>
        {years.map(y => <option key={y} value={y}>{gematriya(y)}</option>)}
      </select>
    </Wrap>
  );
};

const HebrewDateInput: React.FC<{
  value: string;                       // Gregorian ISO yyyy-mm-dd ('' = empty)
  onChange: (iso: string) => void;
  className?: string;                  // applied to the inputs/selects
  style?: React.CSSProperties;         // applied to the inputs/selects
  /** Which calendar the picker opens on. Default: לועזי. */
  defaultMode?: 'greg' | 'hebrew';
}> = ({ value, onChange, className, style, defaultMode }) => {
  const [mode, setMode] = useState<'greg' | 'hebrew'>(defaultMode || 'greg');

  // `??` (not `||`) so a caller that styles the inputs purely with `style`
  // can pass an empty className and opt out of the Tailwind defaults.
  const sel = className ?? 'w-full px-2 py-2 text-sm border border-gray-300 rounded-lg dark:bg-gray-700 dark:border-gray-600';

  return (
    <div>
      <div className="flex items-center gap-2">
        {mode === 'greg' ? (
          <input type="date" dir="ltr" className={sel} style={style} value={value} onChange={e => onChange(e.target.value)} />
        ) : (
          <HebrewParts value={value} onChange={onChange} className={sel} style={style} />
        )}
        <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5 flex-shrink-0">
          {([['greg', 'לועזי'], ['hebrew', 'עברי']] as const).map(([v, l]) => (
            <button key={v} type="button" onClick={() => setMode(v)}
              className={`px-2 py-1 text-[11px] font-semibold rounded-md transition ${mode === v ? 'bg-white dark:bg-gray-700 text-indigo-700 shadow-sm' : 'text-gray-500'}`}>{l}</button>
          ))}
        </div>
      </div>
      {value && mode === 'greg' && <p className="text-[11px] text-gray-400 mt-1">{hebrewDateLabel(value)}</p>}
      {value && mode === 'hebrew' && <p className="text-[11px] text-gray-400 mt-1">{gregDateLabel(value)}</p>}
    </div>
  );
};

/**
 * Compact date field for signature documents: a ruled line showing the picked
 * date as text, and a popover with the Hebrew (and optionally Gregorian)
 * picker. The stored value stays Gregorian ISO like everywhere else, so
 * submissions, PDFs and the tuition intake keep reading one canonical format.
 */
export const InlineHebrewDate: React.FC<{
  value: string;
  onChange: (iso: string) => void;
  label: string;
  primary?: string;
  /** 'hebrew' = Hebrew only; 'both' = Hebrew picker + לועזי tab, both shown. */
  mode: 'hebrew' | 'both';
  widthCh?: number;
}> = ({ value, onChange, label, primary, mode, widthCh }) => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'hebrew' | 'greg'>('hebrew');

  const heb = hebrewDateLabel(value);
  const greg = gregDateLabel(value);
  const selCls = 'px-1.5 py-1 text-xs border border-gray-300 rounded-md bg-white outline-none';

  // `sig-field` lets a signature document stack this on its own row on a phone,
  // like every other inline field (see the .sig-doc styles in PublicTool).
  return (
    <span className="sig-field relative inline-block mx-1 align-baseline" style={{ minWidth: `${widthCh || 14}ch` }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{ borderBottomColor: value ? primary : undefined, minWidth: `${widthCh || 14}ch` }}
        className="sig-field-btn inline-block border-b-2 border-gray-400 bg-transparent px-1 py-0.5 text-sm text-center leading-normal"
      >
        {value
          ? <>
              <span>{heb}</span>
              {mode === 'both' && greg && <span className="text-gray-400 text-[0.85em]"> ({greg})</span>}
            </>
          : <span className="text-gray-400">{label}</span>}
      </button>
      {open && (
        <>
          {/* click-away layer — a span so the popover stays valid inside <p> */}
          <span className="fixed inset-0 z-40 block" onClick={() => setOpen(false)} />
          <span className="absolute z-50 block bg-white border border-gray-200 shadow-xl rounded-xl p-2 mt-1 text-right"
            style={{ top: '100%', insetInlineEnd: 0, minWidth: 250 }} dir="rtl">
            {mode === 'both' && (
              <span className="flex bg-gray-100 rounded-lg p-0.5 mb-2">
                {([['hebrew', 'עברי'], ['greg', 'לועזי']] as const).map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setTab(v)}
                    className={`flex-1 px-2 py-1 text-[11px] font-semibold rounded-md transition ${tab === v ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-500'}`}>{l}</button>
                ))}
              </span>
            )}
            <span className="flex items-center gap-1">
              {mode === 'both' && tab === 'greg'
                ? <input type="date" dir="ltr" className={`${selCls} flex-1`} value={value || ''} onChange={e => onChange(e.target.value)} />
                : <HebrewParts value={value} onChange={onChange} className={selCls} as="span" />}
            </span>
            <span className="flex items-center justify-between gap-2 mt-2 text-[11px]">
              <span className="text-gray-400">{value ? (tab === 'greg' && mode === 'both' ? heb : greg) : ''}</span>
              <span className="flex gap-2">
                <button type="button" className="text-gray-500 hover:text-red-600" onClick={() => { onChange(''); }}>נקה</button>
                <button type="button" className="font-semibold text-indigo-600" onClick={() => setOpen(false)}>סגור</button>
              </span>
            </span>
          </span>
        </>
      )}
    </span>
  );
};

export default HebrewDateInput;
